import { z } from 'zod'
import invariant from 'tiny-invariant'
import type { JudiciaryMonitoringLegalField } from '../categories'
import type { IDecision } from '../norms.types'
import { refsSchema } from '../parser/refsSchema'
import { urlify } from '../urlify'

export const courts = [
  'OGH',
  'VfGH',
  'VwGH',
  'BVwG',
  'LG',
  'AUSL',
  'BG',
  'BFG',
  'DSB',
  'EuGH',
  'EuG',
] as const
export type Courts = (typeof courts)[number]

export interface JudiciaryMonitoringLegalFieldSubscription {
  topic: JudiciaryMonitoringLegalField
  courts: Courts[]
  laws: string[]
  /** Keywords to be used to include decisions */
  keywords: string[]
  /** Keywords to be used to exclude decisions */
  negativeKeywords: string[]
  /**
   * From which score onwards decisions should be included. 5 is very strict, 3 is very lax
   * @default 5
   */
  minScore?: 3 | 4 | 5
  /** Email frequency for this subscription */
  emailFrequency?: string
  /** If true, this category/legal field should not be processed and no emails should be sent */
  archived?: boolean
}

type CourtMapping = { abbr: string; name: string; euPath?: string }

const courtOfJustice: CourtMapping = {
  abbr: 'EuGH',
  name: 'Court of Justice',
  euPath: 'gerichtshof',
}
const generalCourt: CourtMapping = {
  abbr: 'EuG',
  name: 'General Court',
  euPath: 'gericht',
}
const civilServiceTribunal: CourtMapping = {
  abbr: 'GöD',
  name: 'Civil Service Tribunal',
  euPath: 'gericht-fuer-den-oeffentlichen-dienst',
}

export const judikaturApplikationMappings: Record<string, CourtMapping> = {
  dsk: { abbr: 'DSB', name: 'Datenschutzbehörde' },
  asylgh: { abbr: 'AsylGH', name: 'Asylgerichtshof' },
  bks: { abbr: 'BKS', name: 'Bundeskommunikationssenat' },
  bvwg: { abbr: 'BVwG', name: 'Bundesverwaltungsgericht' },
  bwvgh: { abbr: 'BVwG', name: 'Bundesverwaltungsgericht' },
  dok: { abbr: 'DOK', name: 'Disziplinarkommissionen' },
  gbk: { abbr: 'GBK', name: 'Gleichbehandlungskommissionen' },
  justiz: { abbr: 'Justiz', name: 'Justiz' },
  lvwg: { abbr: 'LVwG', name: 'Landesverwaltungsgerichte' },
  pvak: { abbr: 'PVAB', name: 'Personalvertretungsaufsichtsbehörde' },
  ubas: { abbr: 'UBAS', name: 'Unabhängige Verwaltungssenate' },
  upts: { abbr: 'UPTS', name: 'Unabhängiger Parteien-Transparenz-Senat' },
  uvs: { abbr: 'UVS', name: 'Unabhängige Verwaltungssenate' },
  vfgh: { abbr: 'VfGH', name: 'Verfassungsgerichtshof' },
  vwgh: { abbr: 'VwGH', name: 'Verwaltungsgerichtshof' },
  ogh: { abbr: 'OGH', name: 'Oberster Gerichtshof' },
  bfg: { abbr: 'BFG', name: 'Bundesfinanzgericht' },
  'court of justice': courtOfJustice,
  'general court': generalCourt,
  'civil service tribunal': civilServiceTribunal,
  // EUR-Lex notices are fetched in German, so a stored EU decision carries the
  // German `CREATED_BY` label, never the English one.
  gerichtshof: courtOfJustice,
  gericht: generalCourt,
  'gericht für den öffentlichen dienst': civilServiceTribunal,
}

// Reverse lookup: long display name (lowercased) -> mapping entry. Lets
// `getCourtAbbreviation` normalize raw institution strings like
// "Verwaltungsgerichtshof" or "Oberster Gerichtshof" (not just RIS
// application codes like "vwgh") to their canonical abbreviation.
//
// Ambiguous names (same `name` resolving to different `abbr`s, e.g.
// `ubas`/`uvs` both named "Unabhängige Verwaltungssenate") are dropped
// from the lookup — we cannot pick one safely.
const nameMappings: Record<string, CourtMapping> = {}
const ambiguousNames = new Set<string>()
for (const entry of Object.values(judikaturApplikationMappings)) {
  const key = entry.name.toLowerCase()
  const existing = nameMappings[key]
  if (existing && existing.abbr !== entry.abbr) {
    ambiguousNames.add(key)
    continue
  }
  nameMappings[key] = entry
}
for (const key of ambiguousNames) {
  delete nameMappings[key]
}

/**
 * Maps a raw institution string to its display abbreviation. Case-insensitive.
 * Accepts RIS application codes (`'vwgh'`), EU long names
 * (`'Court of Justice'`), and AT long names (`'Verwaltungsgerichtshof'`).
 * Returns the input unchanged if no mapping exists.
 *
 * @example
 * getCourtAbbreviation('Court of Justice')      // 'EuGH'
 * getCourtAbbreviation('vwgh')                  // 'VwGH'
 * getCourtAbbreviation('Verwaltungsgerichtshof')// 'VwGH'
 * getCourtAbbreviation('dsk')                   // 'DSB'
 * getCourtAbbreviation('UnknownCourt')          // 'UnknownCourt'
 */
export function getCourtAbbreviation(institution: string): string {
  const key = institution.toLowerCase()
  return (
    judikaturApplikationMappings[key]?.abbr ??
    nameMappings[key]?.abbr ??
    institution
  )
}

/**
 * Display label for a court group. Returns the canonical abbreviation
 * (e.g. `OGH`, `EuGH`) when known, otherwise prefers `originalInstitution`
 * over `key` so unmapped courts render with their original casing
 * (`"OLG Wien"`) instead of the lowercased grouping key (`"olg wien"`).
 *
 * Useful when consumers `groupBy(decisions, d => d.institution.toLowerCase())`:
 * pass the lowercased key and the first decision's raw `institution` to
 * recover the original casing for unmapped courts.
 *
 * @example
 * getCourtLabel('ogh', 'OGH')               // 'OGH'         (mapped)
 * getCourtLabel('court of justice', '...')  // 'EuGH'        (mapped)
 * getCourtLabel('olg wien', 'OLG Wien')     // 'OLG Wien'    (original casing)
 * getCourtLabel('olg wien')                 // 'olg wien'    (no original)
 */
export function getCourtLabel(
  key: string,
  originalInstitution?: string,
): string {
  const mapped = getCourtAbbreviation(key)
  if (mapped !== key) return mapped
  return originalInstitution && originalInstitution.length > 0
    ? originalInstitution
    : mapped
}

// Reverse lookup: abbreviation (lowercased) -> mapping entry. Lets
// `getCourtPath` accept canonical abbreviations like "EuGH" or "VwGH"
// (not just raw institution strings like "Court of Justice") and still
// resolve EU courts to their `/eu-judikatur/{euPath}` route.
//
// Throws at module load if two forward entries share an `abbr` but
// disagree on `euPath` — silently picking one based on iteration order
// would route some EU courts to the wrong path. Same-shape duplicates
// (e.g. `bvwg`/`bwvgh` -> `BVwG`) are allowed.
const abbreviationMappings: Record<string, CourtMapping> = {}
for (const entry of Object.values(judikaturApplikationMappings)) {
  const key = entry.abbr.toLowerCase()
  const existing = abbreviationMappings[key]
  if (existing && existing.euPath !== entry.euPath) {
    throw new Error(
      `judikaturApplikationMappings: conflicting reverse-lookup for abbr "${entry.abbr}" (euPath "${existing.euPath ?? ''}" vs "${entry.euPath ?? ''}")`,
    )
  }
  abbreviationMappings[key] = entry
}

/**
 * Returns the URL path for a court's decision listing page.
 * EU courts route to `/eu-judikatur/{euPath}`, AT courts to `/judikatur/{abbr}`.
 * Case-insensitive. Accepts raw institution strings or canonical abbreviations.
 *
 * Unmapped institutions (e.g. raw `"OLG Wien"`) are URL-safe-slugified via
 * `urlify` so downstream consumers like `getDecisionUrl` and the email digest
 * never generate paths with literal spaces.
 *
 * @example
 * getCourtPath('Court of Justice')      // '/eu-judikatur/gerichtshof'
 * getCourtPath('EuGH')                  // '/eu-judikatur/gerichtshof'
 * getCourtPath('General Court')         // '/eu-judikatur/gericht'
 * getCourtPath('EuG')                   // '/eu-judikatur/gericht'
 * getCourtPath('ogh')                   // '/judikatur/ogh'
 * getCourtPath('Oberster Gerichtshof')  // '/judikatur/ogh'
 * getCourtPath('VwGH')                  // '/judikatur/vwgh'
 * getCourtPath('OLG Wien')              // '/judikatur/olg-wien'
 */
export function getCourtPath(institutionOrAbbr: string): string {
  const key = institutionOrAbbr.toLowerCase()
  const mapping =
    judikaturApplikationMappings[key] ??
    abbreviationMappings[key] ??
    nameMappings[key]
  if (mapping?.euPath) {
    return `/eu-judikatur/${mapping.euPath}`
  }
  const abbr = mapping?.abbr ?? institutionOrAbbr
  return `/judikatur/${urlify(abbr)}`
}

/**
 * Sometimes there are
 *  - multiple institutions separated by a comma or semicolon
 *  - spaces replaced by an underscore
 *
 * Examples of non-correct institution names:
 *  - "AUSL EGMR, OGH, AUSL_BGH"
 *  - "AUSL EKMR; AUSL EGMR; OGH, AUSL EGMR"
 *  - "OGH; AUSL EGMR"
 *  - "OGH,AUSL EGMR"
 *
 * This function fixes these issues and returns the new names.
 * @param name The institution name, from one of the following decision properties: `Gericht`, `Berhoerde`, or `Organ`
 * @returns A list of "fixed" institution names
 */
export function fixInstitutionNames(name: string): string[] {
  return name.split(/[,;]/).map((gericht: string) => {
    return gericht.trim().split('_').join(' ')
  })
}

/**
 * Returns the normalized institution name(s) for a decision.
 *
 * Expects decisions that have been run through `normalizeJudikaturDecisionData`,
 * which populates the generalized `institution` field from provider-specific
 * metadata (e.g. RIS `Applikation`/`Gericht`/`Organ`). Comma/semicolon-separated
 * values are split, and known Applikation identifiers are mapped to their
 * display abbreviation (e.g. "Bvwg" -> "BVwG").
 */
export function getInstitutionName(decision: IDecision): string[] {
  invariant(
    decision.institution,
    `Missing institution on decision ${decision.normId}`,
  )

  return fixInstitutionNames(decision.institution).map(
    (name) => judikaturApplikationMappings[name.toLowerCase()]?.abbr ?? name,
  )
}

export const summarySchema = z.object({
  title: z
    .string()
    .describe(
      'Ausführliche Überschrift, mit Fokus auf den Sachverhalt (was). Erwähne NICHT das Gericht oder wie entschieden wurde.',
    ),
  verdict: z
    .string()
    .describe('Eine prägnante Zusammenfassung vom Spruch der Entscheidung.'),
  facts: z
    .string()
    .describe(
      'Ausgiebige Darstellung der wesentlichen Sachverhalte. Wer ist involviert? Was ist passiert? Ausgiebig.',
    ),
  keywords: z
    // Sonnet 5 on Bedrock runs structured output as an unenforced JSON tool
    // (no constrained decoding) and reliably emits this field as ONE
    // comma-joined string. Accept both shapes and normalize to string[].
    .union([
      z.array(z.string()),
      z.string().transform((s) =>
        s
          .split(/\s*[,;·|]\s*/)
          .map((k) => k.trim())
          .filter(Boolean),
      ),
    ])
    .describe('JSON-Array von bis zu 5 Schlagwörtern'),
})

export const decisionSummarySchema = summarySchema.extend({
  legalAssessment: z.string(),
  version: z.number(),
})

export const keywordMatchSchema = z.object({
  matchingKeywords: z.array(z.string()),
  keywordsScore: z.number().min(1).max(5),
})

export const keywordClassificationSchema = keywordMatchSchema.extend({
  negativeMatchingKeywords: z.array(z.string()).optional(),
  negativeKeywordsScore: z.number().min(1).max(5).int().optional(),
})

export type DecisionSummary = z.infer<typeof decisionSummarySchema>

/**
 * Response shape of the `/summarize` API: the persisted summary fields plus
 * extracted refs. All fields optional because legacy stored summaries may be
 * partial; `version` is internal-only and not surfaced to clients.
 */
export const summaryResponseSchema = decisionSummarySchema
  .omit({ version: true })
  .partial()
  .extend({ refs: refsSchema.optional() })

export type SummaryResponse = z.infer<typeof summaryResponseSchema>

export interface CategoryClassification {
  reasoning: string
  categoryScore: number
}

export type KeywordMatch = z.infer<typeof keywordMatchSchema>

export type KeywordClassification = z.infer<typeof keywordClassificationSchema>

export type DecisionCategorization = CategoryClassification &
  Partial<KeywordClassification>
